// Social Media Calendar Component
import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, Send, Edit, Trash2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { socialMediaService } from '../services/socialMediaService';
import { SocialPost } from '../types/social.types';

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  time: string;
  platforms: string[];
  status: 'scheduled' | 'published' | 'draft';
  content: string;
}

const SocialMediaCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Załaduj posty przy montowaniu
  useEffect(() => {
    loadPosts();
  }, [currentDate]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const allPosts = await socialMediaService.getPosts({
        dateFrom: startOfMonth,
        dateTo: endOfMonth
      });
      
      setPosts(allPosts);
      
      // Konwertuj posty na wydarzenia kalendarza
      const calendarEvents: CalendarEvent[] = allPosts.map(post => ({
        id: post.id,
        title: post.content.substring(0, 50) + '...',
        date: new Date(post.scheduled_at || post.created_at),
        time: new Date(post.scheduled_at || post.created_at).toLocaleTimeString('pl-PL', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        platforms: post.platforms,
        status: post.status,
        content: post.content
      }));
      
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pomocnicze funkcje kalendarza
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  };

  const changeMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => 
      event.date.getDate() === date.getDate() &&
      event.date.getMonth() === date.getMonth() &&
      event.date.getFullYear() === date.getFullYear()
    );
  };

  const handleSchedulePost = async (date: Date, time: string) => {
    // Otwórz composer z predefiniowaną datą
    const scheduledDate = new Date(date);
    const [hours, minutes] = time.split(':');
    scheduledDate.setHours(parseInt(hours), parseInt(minutes));
    
    // Tu można otworzyć modal lub przekierować do composera
    console.log('Schedule post for:', scheduledDate);
    setShowAddEvent(false);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    
    // Puste komórki na początku
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Dni miesiąca
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      const isToday = 
        date.getDate() === new Date().getDate() &&
        date.getMonth() === new Date().getMonth() &&
        date.getFullYear() === new Date().getFullYear();
      const isSelected = 
        selectedDate &&
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
      
      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`
            min-h-[100px] p-2 border border-zinc-700 cursor-pointer transition-all
            ${isToday ? 'bg-blue-900/20 border-blue-500' : 'hover:bg-zinc-700'}
            ${isSelected ? 'ring-2 ring-blue-500' : ''}
          `}
        >
          <div className="flex justify-between items-start mb-1">
            <span className={`text-sm font-medium ${isToday ? 'text-blue-400' : 'text-gray-300'}`}>
              {day}
            </span>
            {dayEvents.length > 0 && (
              <span className="text-xs bg-blue-600 text-white px-1 rounded">
                {dayEvents.length}
              </span>
            )}
          </div>
          
          {/* Mini wydarzenia */}
          <div className="space-y-1">
            {dayEvents.slice(0, 2).map((event, idx) => (
              <div
                key={idx}
                className={`text-xs p-1 rounded truncate ${
                  event.status === 'published' 
                    ? 'bg-green-900/50 text-green-400'
                    : event.status === 'scheduled'
                    ? 'bg-blue-900/50 text-blue-400'
                    : 'bg-zinc-600 text-gray-400'
                }`}
              >
                {event.time} • {event.platforms.join(', ')}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-xs text-gray-500">
                +{dayEvents.length - 2} więcej
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Kalendarz */}
      <div className="lg:col-span-2 bg-zinc-800 rounded-lg shadow-xl border border-zinc-700">
        {/* Nagłówek kalendarza */}
        <div className="p-4 border-b border-zinc-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-blue-500" />
            {getMonthName(currentDate)}
          </h2>
          
          <div className="flex gap-2">
            <button
              onClick={() => changeMonth('prev')}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-all"
            >
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 bg-zinc-700 text-gray-300 rounded-lg hover:bg-zinc-600 text-sm"
            >
              Dziś
            </button>
            <button
              onClick={() => changeMonth('next')}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-all"
            >
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Dni tygodnia */}
        <div className="grid grid-cols-7 border-b border-zinc-700">
          {['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-400">
              {day}
            </div>
          ))}
        </div>
        
        {/* Siatka kalendarza */}
        <div className="grid grid-cols-7 p-2">
          {loading ? (
            <div className="col-span-7 flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            renderCalendarDays()
          )}
        </div>
      </div>
      
      {/* Panel boczny */}
      <div className="space-y-4">
        {/* Przycisk dodawania */}
        <button
          onClick={() => setShowAddEvent(true)}
          className="w-full p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Zaplanuj nowy post
        </button>
        
        {/* Wydarzenia wybranego dnia */}
        {selectedDate && (
          <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
            <h3 className="text-lg font-semibold text-white mb-3">
              {selectedDate.toLocaleDateString('pl-PL', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long' 
              })}
            </h3>
            
            <div className="space-y-3">
              {getEventsForDate(selectedDate).length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  Brak zaplanowanych postów
                </p>
              ) : (
                getEventsForDate(selectedDate).map(event => (
                  <div
                    key={event.id}
                    className="p-3 bg-zinc-700 rounded-lg border border-zinc-600"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-white">{event.time}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        event.status === 'published'
                          ? 'bg-green-600 text-white'
                          : event.status === 'scheduled'
                          ? 'bg-blue-600 text-white'
                          : 'bg-zinc-600 text-gray-300'
                      }`}>
                        {event.status === 'published' ? 'Opublikowano' : 
                         event.status === 'scheduled' ? 'Zaplanowano' : 'Szkic'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-300 mb-2 line-clamp-2">
                      {event.content}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        {event.platforms.map(platform => (
                          <span key={platform} className="text-xs">
                            {platform === 'facebook' && '📘'}
                            {platform === 'linkedin' && '💼'}
                            {platform === 'instagram' && '📸'}
                            {platform === 'twitter' && '🐦'}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex gap-1">
                        <button className="p-1 hover:bg-zinc-600 rounded">
                          <Edit className="w-4 h-4 text-gray-400" />
                        </button>
                        <button className="p-1 hover:bg-zinc-600 rounded">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Statystyki miesiąca */}
        <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-3">
            Podsumowanie miesiąca
          </h3>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Zaplanowane:</span>
              <span className="text-white font-medium">
                {events.filter(e => e.status === 'scheduled').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Opublikowane:</span>
              <span className="text-white font-medium">
                {events.filter(e => e.status === 'published').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Szkice:</span>
              <span className="text-white font-medium">
                {events.filter(e => e.status === 'draft').length}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal dodawania wydarzenia */}
      {showAddEvent && selectedDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-white mb-4">
              Zaplanuj post na {selectedDate.toLocaleDateString('pl-PL')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Godzina</label>
                <input
                  type="time"
                  className="w-full p-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                  defaultValue="12:00"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAddEvent(false)}
                  className="px-4 py-2 bg-zinc-700 text-gray-300 rounded-lg hover:bg-zinc-600"
                >
                  Anuluj
                </button>
                <button
                  onClick={() => handleSchedulePost(selectedDate, '12:00')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Przejdź do tworzenia
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaCalendar;
